/**
 * Client-side of `/v1/auth/firebase/callback` — the sole auth ingress
 * post-plan-032.
 *
 * The caller acquires a Firebase ID token (via
 * `signUpWithPassword` / `signInWithPassword` / `signInWithGoogle`)
 * and hands it here; we exchange it for a Backstop session and decide
 * where to route next.
 *
 * Wrap-key derivation is orthogonal — SignupRoute / LoginRoute call
 * `storeFamilyKey(email, password, ...)` / `tryLoadFamilyKey(email,
 * password)` around this exchange for the password path; the Firebase-
 * device-key path is handled here for accounts without a password to
 * derive from (Google signup + fresh device restore).
 */

import { api } from '../state/api';
import { setAuthSession, storeFamilyKeyFirebase } from '../state/session';
import { loadFamilyKeyForFirebase } from '../e2ee/deviceKey';

interface CallbackResponse {
  mode: 'signup' | 'login';
  parent_id: string;
  family_id: string;
  access_token: string;
  refresh_token: string;
  linked?: boolean;
}

/**
 * The three routes the client can end up on after a Firebase sign-in.
 * The signup + login pages read `next` to know what to render next.
 */
export type FirebaseNext =
  /** Fresh account — send the parent to the mnemonic-reveal screen. */
  | { kind: 'reveal-mnemonic' }
  /** Returning user, device already has the wrapped family key. Straight to dashboard. */
  | { kind: 'dashboard' }
  /** Returning user, fresh device. Prompt for the recovery phrase. */
  | { kind: 'restore-mnemonic' };

export interface FirebaseSignInResult {
  next: FirebaseNext;
  parentId: string;
  familyId: string;
  linked: boolean;
}

/**
 * Options for [`completeFirebaseCallback`]. `provider` drives the
 * default `next` computation for signup mode:
 *
 *  - `'google.com'`: no password to derive from; we generate a random
 *    family key + wrap it under a fresh device-local AES-GCM key
 *    (Firebase-path vault). Caller shows the mnemonic-reveal screen.
 *  - `'password'`: SignupRoute already ran `storeFamilyKey` with the
 *    plaintext password before calling this, so the vault is
 *    populated. We don't touch the Firebase-path vault here.
 */
export interface CompleteOptions {
  email: string;
  idToken: string;
  provider: 'google.com' | 'password';
}

/**
 * Exchange the Firebase ID token for a Backstop session, set the
 * session, and compute the post-signup routing decision.
 */
export async function completeFirebaseCallback(
  opts: CompleteOptions,
): Promise<FirebaseSignInResult> {
  const resp = await api<CallbackResponse>('/v1/auth/firebase/callback', {
    method: 'POST',
    body: JSON.stringify({ id_token: opts.idToken }),
  });

  setAuthSession({
    accessToken: resp.access_token,
    refreshToken: resp.refresh_token,
    parentId: resp.parent_id,
    familyId: resp.family_id,
    email: opts.email,
  });

  if (resp.mode === 'signup') {
    if (opts.provider === 'google.com') {
      // Fresh family via Google — generate the family key and wrap
      // it under the device-local AES-GCM key. Caller routes to
      // mnemonic reveal.
      const familyKey = crypto.getRandomValues(new Uint8Array(32));
      await storeFamilyKeyFirebase(opts.email, familyKey);
    }
    // For provider='password' the SignupRoute already ran
    // storeFamilyKey(email, password, familyKey) before hitting us —
    // the vault is populated + familyKeyInMemory is set.
    return {
      next: { kind: 'reveal-mnemonic' },
      parentId: resp.parent_id,
      familyId: resp.family_id,
      linked: false,
    };
  }

  // mode === 'login'. Existing account — is this device already
  // unlocked? For Google, check the Firebase-path vault. For
  // password, the LoginRoute already handled the unlock with
  // tryLoadFamilyKey before calling us, so we don't touch the
  // vault here — just route to dashboard if the login succeeded
  // upstream.
  if (opts.provider === 'password') {
    return {
      next: { kind: 'dashboard' },
      parentId: resp.parent_id,
      familyId: resp.family_id,
      linked: resp.linked === true,
    };
  }
  const existing = await loadFamilyKeyForFirebase(opts.email);
  return {
    next:
      existing !== null ? { kind: 'dashboard' } : { kind: 'restore-mnemonic' },
    parentId: resp.parent_id,
    familyId: resp.family_id,
    linked: resp.linked === true,
  };
}
